// src/products/dataroom/pages/PartiesPage.js
import React, { useEffect, useState } from 'react';
import { Building2, ChevronDown, ChevronRight, AlertCircle, Plus, RefreshCw, Edit2 } from 'lucide-react';
import useDataroomStore from '../state/dataroomStore';
import AddPartyModal from '../components/Modals/AddPartyModal';
import RenameModal from '../components/Modals/RenameModal';
import partyTeamService from '../services/partyTeamService';
import { formatDate } from '../utils/dateUtils';
import { supabase } from '../../../config';

const PartiesPage = ({ userRole, onMessage }) => {
  const {
    parties,
    projects,
    expandedParties,
    loading,
    errors,
    loadAllParties,
    loadProjects,
    togglePartyExpansion
  } = useDataroomStore();

  const isLoadingParties = loading?.parties || false;
  const partiesError = errors?.parties || null;

  const [showAddPartyModal, setShowAddPartyModal] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [renameParty, setRenameParty] = useState(null);
  const [projectNames, setProjectNames] = useState({}); // Cache for project names
  const canManage = userRole === 'creator' || userRole === 'support';

  const handleAddPartyClick = () => {
    if (projects.length === 0) {
      onMessage && onMessage('Please create a Dealroom project first before adding parties', 'warning');
      return;
    }
    setShowAddPartyModal(true);
  };

  useEffect(() => {
    loadAllParties();
  }, [loadAllParties]);

  // Load projects if not already loaded
  useEffect(() => {
    const loadProjectsIfNeeded = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && projects.length === 0) {
        await loadProjects(user.id);
      }
    };
    loadProjectsIfNeeded();
  }, [projects.length, loadProjects]);

  // Fetch project names for any missing projects
  useEffect(() => {
    const fetchMissingProjectNames = async () => {
      const projectIds = [...new Set(parties.map(p => p.project_id))];
      const missingIds = projectIds.filter(id => {
        const project = projects.find(p => p.id === id);
        return !project;
      });

      if (missingIds.length > 0) {
        const { data, error } = await supabase
          .from('dataroom_projects')
          .select('id, project_name')
          .in('id', missingIds);

        if (!error && data) {
          const namesMap = {};
          data.forEach(p => {
            namesMap[p.id] = p.project_name;
          });
          setProjectNames(prev => ({ ...prev, ...namesMap }));
        }
      }
    };

    if (parties.length > 0) {
      fetchMissingProjectNames();
    }
  }, [parties, projects]);

  // Group parties by project
  const partiesByProject = parties.reduce((acc, party) => {
    if (!acc[party.project_id]) {
      acc[party.project_id] = [];
    }
    acc[party.project_id].push(party);
    return acc;
  }, {});

  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const handleRenameParty = async (updatedParty) => {
    try {
      const result = await partyTeamService.updateParty(updatedParty.id, {
        name: updatedParty.name,
        code: updatedParty.code,
        description: updatedParty.description
      });

      if (result.error) throw result.error;

      onMessage && onMessage(`Party renamed successfully`, 'success');
      loadAllParties();
    } catch (error) {
      console.error('Error renaming party:', error);
      onMessage && onMessage('Failed to rename party: ' + error.message, 'error');
      throw error;
    }
  };

  if (isLoadingParties) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading parties...</p>
        </div>
      </div>
    );
  }

  if (partiesError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Parties</h3>
            <p className="text-sm text-red-700 mt-1">{partiesError}</p>
          </div>
        </div>
        <button
          onClick={loadAllParties}
          className="mt-4 flex items-center px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Building2 className="w-8 h-8 mr-3 text-blue-600" />
            Parties Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage parties across all DealRooms
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadAllParties}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          {canManage && (
            <button
              onClick={handleAddPartyClick}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Party
            </button>
          )}
        </div>
      </div>

      {/* Parties Grouped by DealRoom */}
      {parties.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Parties Created</h3>
          <p className="text-gray-500 mb-6">
            Parties are organizational groups that have access to DealRooms.
          </p>
          {canManage && (
            <button
              onClick={handleAddPartyClick}
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Your First Party
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(partiesByProject).map(([projectId, projectParties]) => {
            const project = projects.find(p => p.id === parseInt(projectId));
            const projectName = project?.project_name || projectNames[parseInt(projectId)] || null;
            const isProjectExpanded = expandedProjects[projectId] !== false; // Default expanded
            
            return (
              <div key={projectId} className="bg-white rounded-lg shadow overflow-hidden border-2 border-gray-200">
                {/* DealRoom Header */}
                <div
                  className="bg-gradient-to-r from-blue-50 to-blue-100 border-b-2 border-blue-200 cursor-pointer"
                  onClick={() => toggleProjectExpansion(projectId)}
                >
                  <div className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center">
                      {isProjectExpanded ? (
                        <ChevronDown className="w-6 h-6 text-blue-600 mr-3" />
                      ) : (
                        <ChevronRight className="w-6 h-6 text-blue-600 mr-3" />
                      )}
                      <Building2 className="w-6 h-6 text-blue-600 mr-3" />
                      <div>
                        <h2 className="text-lg font-bold text-blue-900">
                          Dealroom: {projectName || 'Unknown Dealroom'}
                        </h2>
                        <p className="text-sm text-blue-700">
                          {projectParties.length} part{projectParties.length === 1 ? 'y' : 'ies'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {projectParties.map(p => (
                        <div
                          key={p.id}
                          className="w-6 h-6 rounded-md shadow-sm"
                          style={{ backgroundColor: p.color }}
                          title={p.name}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Parties in this DealRoom */}
                {isProjectExpanded && (
                  <div className="divide-y divide-gray-200">
                    {[...projectParties].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((party, partyIndex) => {
                      const isExpanded = expandedParties[party.id];
                      const partyNumber = partyIndex + 1;
                      return (
                        <div key={party.id} className="border-l-4 hover:bg-gray-50 transition-colors" style={{ borderLeftColor: party.color }}>
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer"
                            onClick={() => togglePartyExpansion(party.id)}
                          >
                            <div className="flex items-center">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                                {isExpanded ? (
                                  <ChevronDown className="w-5 h-5" style={{ color: party.color }} />
                                ) : (
                                  <ChevronRight className="w-5 h-5" style={{ color: party.color }} />
                                )}
                              </div>
                              <div
                                className="w-10 h-10 rounded-lg mr-3 flex items-center justify-center shadow-sm"
                                style={{ backgroundColor: party.color }}
                              >
                                <span className="text-white font-bold text-sm">{party.code}</span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="text-base font-semibold text-gray-900">Party {partyNumber}: {party.name}</h3>
                                  {canManage && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setRenameParty(party);
                                      }}
                                      className="text-gray-400 hover:text-purple-600 transition-colors"
                                      title="Rename party"
                                    >
                                      <Edit2 className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">{party.description || 'No description'}</p>
                              </div>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 ml-16">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="font-medium text-gray-700">Type:</span>
                                  <span className="ml-2 text-gray-600">{party.party_type || 'custom'}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Created:</span>
                                  <span className="ml-2 text-gray-600">
                                    {formatDate(party.created_at)}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Code:</span>
                                  <span className="ml-2 text-gray-600 font-mono">{party.code}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Color:</span>
                                  <div className="inline-flex items-center ml-2">
                                    <div 
                                      className="w-4 h-4 rounded border border-gray-300 mr-2"
                                      style={{ backgroundColor: party.color }}
                                    />
                                    <span className="text-gray-600 font-mono text-xs">{party.color}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Party Modal */}
      <AddPartyModal
        isOpen={showAddPartyModal}
        onClose={() => setShowAddPartyModal(false)}
        onComplete={(addedParties) => {
          onMessage && onMessage(`Successfully created ${addedParties.length} part${addedParties.length === 1 ? 'y' : 'ies'}`, 'success');
          loadAllParties();
        }}
      />

      {/* Rename Party Modal */}
      <RenameModal
        isOpen={!!renameParty}
        onClose={() => setRenameParty(null)}
        item={renameParty}
        itemType="party"
        onRename={handleRenameParty}
      />
    </div>
  );
};

export default React.memo(PartiesPage);